import { useState } from "react";
import { MENU, MENU_PDF } from "@/lib/eventData";
import SectionHeader from "@/components/gala/SectionHeader";
import { Wine, Download } from "lucide-react";

const TABS = [
  { id: "canapes", label: "Canapés" },
  { id: "mains", label: "Main Course" },
  { id: "desserts", label: "Desserts" },
];

export default function MenuSection() {
  const [active, setActive] = useState("canapes");
  const items = MENU[active] || [];

  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="section relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="The Banquet Card"
          title="Buffet Menu"
          description="A generous, family-style buffet — served from 12:00, followed by dessert at 14:00. Below is a taste of what's on the table."
        />

        {/* Tabs */}
        <div
          role="tablist"
          aria-label="Menu courses"
          data-testid="menu-tabs"
          className="flex flex-wrap gap-2 sm:gap-3 mb-10"
        >
          {TABS.map((t) => {
            const activeState = active === t.id;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={activeState}
                data-testid={`menu-tab-${t.id}`}
                onClick={() => setActive(t.id)}
                className={`px-5 py-2.5 rounded-full font-display text-[10px] sm:text-xs tracking-[0.25em] border transition-all duration-300 ${
                  activeState
                    ? "bg-[#D4AF37] text-[#123A2E] border-[#D4AF37]"
                    : "text-[#E0D5B8] border-[#D4AF37]/30 hover:border-[#D4AF37]/70 hover:text-[#F3E08A]"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {items.map((dish, idx) => (
            <article
              key={`${active}-${idx}`}
              data-testid={`menu-item-${active}-${idx}`}
              className="card-luxe p-7 sm:p-8 rounded-sm rise"
              style={{ animationDelay: `${idx * 60}ms` }}
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <h3 className="font-serif-luxe text-xl sm:text-2xl text-[#F8F1DC] leading-snug">
                  {dish.name}
                </h3>
                {dish.tags && dish.tags.length > 0 && (
                  <div className="flex gap-1.5 shrink-0">
                    {dish.tags.map((tag) => (
                      <span
                        key={tag}
                        className="font-display text-[8px] tracking-[0.2em] text-[#F3E08A] border border-[#D4AF37]/50 px-1.5 py-0.5 rounded-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {dish.desc && (
                <p className="text-[#E0D5B8] text-sm leading-relaxed">
                  {dish.desc}
                </p>
              )}
              {dish.pairing && (
                <div className="mt-5 pt-4 border-t border-[#D4AF37]/15 flex items-center gap-2 text-[#9F7E30]">
                  <Wine size={13} />
                  <span className="text-xs italic text-[#D4AF37]/85">
                    {dish.pairing}
                  </span>
                </div>
              )}
            </article>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-center gap-4 text-xs">
          <a
            data-testid="menu-download-pdf"
            href={MENU_PDF}
            download="Havana_Nights_Menu.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost-gold px-5 py-2.5 rounded-sm inline-flex items-center gap-2 text-[11px]"
          >
            <Download size={13} /> Download Full Menu (PDF)
          </a>
        </div>
      </div>
    </section>
  );
}
